import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'components/common';
import { useAuth } from 'hooks';
import { buildQuery } from 'utils/search';
import { buildQuery as buildPurchasingQuery } from 'utils/purchasing';
import { getFilters, getSearchView } from 'selectors/search';
import { isCurrentSearchAiGenerated } from 'utils/aiSavedSearch';
import { setAlertToCreate, setView } from 'actions/alerts';
import { getAlertToCreate } from 'selectors/alerts';
import { getAISearchData } from 'selectors/searchNLP';

import { CreateModal } from 'components/Alerts/Modal';
import { Tooltip, notification } from 'antd';
import { AlertContentContainer } from '../styled';
import { useHistory } from 'react-router-dom';
import {
  frequencyEnums,
  frequencyOptions,
  frequencyRadioOptions,
} from 'models/frequency';
import { ResearcherSearch } from '../Results/helpers';
import { searchOrigin } from '../Results/constants';
import { DisabledButtonWrapper } from 'components/common/styled';
import { RemoveNonEmptyDate } from '../Filters/keys';

const AlertContent = ({
  isEdit,
  name,
  frequency,
  sharedFrequency,
  sharedWith,
  push,
  dispatch,
}) => {
  const goToAlerts = () => {
    dispatch(setView('savedSearches'));
    push('/searches');
  };

  return (
    <AlertContentContainer>
      Your saved search has been {!isEdit ? 'created' : 'updated'}: {name}.
      <br />
      <br />
      {frequency} has been scheduled for you.
      {sharedWith.length > 0 && (
        <>
          <br />
          {sharedFrequency} has been scheduled for {sharedWith.length}{' '}
          {pluralise('user', sharedWith)}.
        </>
      )}
      <br />
      <br />
      <span onClick={goToAlerts}>Click here</span> to manage your searches.
    </AlertContentContainer>
  );
};

const Alert = ({
  disabled,
  filters: propFilters,
  view: propView,
  searchOrigin: propSearchOrigin,
}) => {
  const dispatch = useDispatch();
  const defaultFilters = useSelector(getFilters);
  const defaultView = useSelector(getSearchView);
  const activeAlert = useSelector(getAlertToCreate);

  const allFilters = propFilters || defaultFilters;
  const view = propView || defaultView;
  const isAiGenerated = useSelector(isCurrentSearchAiGenerated);
  const aiSearchData = useSelector(getAISearchData);
  const { push } = useHistory();
  const {
    user: { userId },
  } = useAuth();

  const handleCreateAlert = frequency => {
    const tmpFilters = { ...allFilters };
    RemoveNonEmptyDate(tmpFilters);

    // Use appropriate buildQuery function based on searchOrigin
    let query, highlightQuery, nestedQueries;
    if (propSearchOrigin === searchOrigin.purchasingSearch) {
      query = buildPurchasingQuery(tmpFilters);
      nestedQueries = null; // purchasing doesn't use nested queries
    } else {
      [query, highlightQuery, , nestedQueries] = buildQuery(tmpFilters, view);
    }

    dispatch(
      setAlertToCreate({
        name: null,
        monthlyKeywords: query,
        highlightQuery,
        structuredSearch: JSON.stringify(tmpFilters),
        userId,
        frequency: frequencyOptions.find(f => f.enumValue === frequency).value,
        frequencyRadio: frequencyRadioOptions[0].label,
        lists: allFilters.lists,
        excludeLists: allFilters.excludeLists,
        searchOrigin:
          propSearchOrigin ||
          (!ResearcherSearch(view)
            ? searchOrigin.organisationSearch
            : searchOrigin.researcherSearch),
        organisationLists: allFilters.organisationLists,
        organisationExcludeLists: allFilters.organisationExcludeLists,
        nestedQueries,
        excludeRecentlyExportedEmails:
          ResearcherSearch(view) && allFilters?.excludeRecentlyExportedEmails,
        isAiGenerated,
        aiSessionId: aiSearchData?.sessionId ?? null,
        childAlert: {
          users: [],
          frequency: frequencyOptions.find(
            f => f.enumValue === frequencyEnums.Monthly
          ).value,
          frequencyRadio: frequencyRadioOptions[0].label,
        },
      })
    );
  };

  const onSuccess = (search, isEdit = false) => {
    notification.open({
      message: 'Search saved',
      description: (
        <AlertContent
          isEdit={isEdit}
          name={search.name}
          frequency={
            search.frequencyRadio === 'Alert' ? search.frequency : 'No Alert'
          }
          sharedFrequency={
            search.childAlert?.frequencyRadio === 'Alert'
              ? search.childAlert?.frequency
              : 'No Alert'
          }
          sharedWith={search.childAlert?.users}
          push={push}
          dispatch={dispatch}
        />
      ),
      placement: 'bottomLeft',
    });
  };

  return (
    <>
      {disabled ? (
        <Tooltip title='Save Search'>
          <DisabledButtonWrapper>
            <Button
              type='secondary'
              data-testid='create-alert'
              size='large'
              disabled={disabled}
              onClick={() => handleCreateAlert(frequencyEnums.Monthly)}
            >
              Save Search
            </Button>
          </DisabledButtonWrapper>
        </Tooltip>
      ) : (
        <Button
          type='secondary'
          data-testid='create-alert'
          size='large'
          onClick={() => handleCreateAlert(frequencyEnums.Monthly)}
        >
          Save Search
        </Button>
      )}

      {activeAlert && <CreateModal onSuccess={onSuccess} />}
    </>
  );
};

const pluralise = (term, arr) => {
  if (arr && arr.filter(x => x !== undefined).length > 1) {
    term += 's';
  }
  return term;
};

export default Alert;
